import clsx from "clsx";
import Autocomplete from "ui/common/components/input/Autocomplete/Autocomplete";
import { useDebounce } from "@commonlibs";
import { useEffect, useRef, useState } from "react";
import type { PartnerDto } from "common/models/api/models/PartnerDto";
import { PartnerAutocompleteProps } from "./PartnerAutocomplete.types";
import usePartnerFindAllWithoutParent from "features/partners/hooks/usePartnerFindAllWithoutParent";
import useUserState from "ui/adapter/state/useUserState";

export default function PartnerAutocomplete({
    className,
    labelKey,
    valueKey,
    onSelectPartner,
    onChange,
    defaultSearch,
    onSearchChange,
    onBlur,
    value: valueProp,
    searchArchived,
    partnerType,
    ...rest
}: PartnerAutocompleteProps): JSX.Element {
    const [search, setSearch] = useState("");
    const [selectedValue, setSelectedValue] = useState<string | PartnerDto | undefined>(valueProp);
    const wasOptionClickedRef = useRef(false);
    const lastSelectedValueRef = useRef<string | PartnerDto | undefined>(valueProp);
    const { selectedRole } = useUserState();
    const { value, handleChange, debouncing } = useDebounce<string>("", { callback: setSearch });
    const organizationAddress = selectedRole?.organization?.address as string;

    const { data: { items = [] } = {}, isFetching } = usePartnerFindAllWithoutParent({
        query: search,
        page: 1,
        order: "ASC",
        orderBy: "address",
        organizationAddress,
        archived: searchArchived,
        type: partnerType,
    });

    useEffect(() => {
        if (defaultSearch !== undefined && defaultSearch !== search) {
            setSearch(defaultSearch);
            handleChange(defaultSearch);
        }
    }, [defaultSearch]);

    useEffect(() => {
        if (onSearchChange && search !== defaultSearch) {
            onSearchChange(search);
        }
        wasOptionClickedRef.current = false;
    }, [search]);

    useEffect(() => {
        if (valueProp !== undefined && valueProp !== selectedValue) {
            setSelectedValue(valueProp);
            lastSelectedValueRef.current = valueProp;
        }
    }, [valueProp]);

    const options = items.map((item) => ({
        value: valueKey ? (item[valueKey] as string) : item,
        label: String(item[labelKey as keyof typeof item] ?? item.address),
    }));

    const handleChangeWrapper = (selectedValueParam: string | PartnerDto) => {
        wasOptionClickedRef.current = true;
        lastSelectedValueRef.current = selectedValueParam;
        setSelectedValue(selectedValueParam);
        onChange?.(selectedValueParam);

        if (onSelectPartner) {
            if (valueKey) {
                const partner = items.find((item) => item[valueKey] === selectedValueParam) ?? null;
                onSelectPartner(partner);
            } else {
                const partner =
                    typeof selectedValueParam === "object" && selectedValueParam !== null && "address" in selectedValueParam
                        ? (selectedValueParam as PartnerDto)
                        : null;
                onSelectPartner(partner);
            }
        }
    };

    const handleBlur = () => {
        if (!onBlur) return;

        const currentValue = wasOptionClickedRef.current
            ? lastSelectedValueRef.current
            : valueProp !== undefined
              ? valueProp
              : selectedValue;

        let isOptionSelected = false;

        if (wasOptionClickedRef.current && currentValue !== undefined) {
            const matchingOption = options.find((option) => {
                if (valueKey) {
                    return option.value === currentValue;
                } else {
                    const optionPartner =
                        typeof option.value === "object" && option.value !== null && "address" in option.value
                            ? (option.value as PartnerDto)
                            : null;
                    const currentPartner =
                        typeof currentValue === "object" && currentValue !== null && "address" in currentValue
                            ? (currentValue as PartnerDto)
                            : null;
                    return currentPartner !== null && optionPartner !== null && currentPartner.address === optionPartner.address;
                }
            });

            if (matchingOption !== undefined) {
                const optionLabel = matchingOption.label.toLowerCase();
                const searchLower = search.toLowerCase();
                isOptionSelected = optionLabel === searchLower || optionLabel.includes(searchLower);
            }
        }

        wasOptionClickedRef.current = false;

        onBlur(search, isOptionSelected);
    };

    return (
        <Autocomplete
            className={clsx("partner-select", className)}
            expandable
            isLoading={isFetching || debouncing}
            onSearchChange={handleChange}
            search={value}
            options={options}
            value={selectedValue}
            onChange={handleChangeWrapper}
            onBlur={handleBlur}
            {...rest}
        />
    );
}
